// CPU matting for the full-resolution still (SA B3). DOM-free.
//  * guidedUpsample: fast guided filter (He et al.) — the low-res segmentation
//    mask is upsampled to the camera resolution following the edges of the
//    luma image, so hair/shoulder edges are sharp instead of a blurry 480-px mask.
//  * decontaminate: push-pull foreground estimation — interior colours are
//    pulled outward into the edge band, and edge pixels use that estimate
//    instead of the camera colour (which is mixed with the old background):
//    composite F·α instead of I·α → no white/coloured halos.
// The live shader (render/stage.ts) uses the same maths and constants.

/** guided-filter regularisation (guide in 0..1) */
export const GF_EPS = 2e-3;
/** decontamination blend: below EDGE_LO alpha use the estimated foreground, above EDGE_HI the camera colour */
export const EDGE_LO = 0.6;
export const EDGE_HI = 0.98;
/** final alpha ramp after the guided filter: removes the soft tails of the low-res mask */
export const ALPHA_LO = 0.12;
export const ALPHA_HI = 0.88;
export const alphaRamp = (a: number) => { const t = Math.max(0, Math.min(1, (a - ALPHA_LO) / (ALPHA_HI - ALPHA_LO))); return t * t * (3 - 2 * t); };
/** interior weight for the push-pull (alpha above this counts as pure foreground) */
export const INTERIOR = 0.92;

/** Separable box mean with radius r (edge-clamped window, normalised by the window actually used). */
export function boxMean(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h), out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let s = 0, n = 0;
    for (let x = 0; x <= Math.min(r, w - 1); x++) { s += src[row + x]; n++; }
    for (let x = 0; x < w; x++) {
      tmp[row + x] = s / n;
      const add = x + r + 1, sub = x - r;
      if (add < w) { s += src[row + add]; n++; }
      if (sub >= 0) { s -= src[row + sub]; n--; }
    }
  }
  for (let x = 0; x < w; x++) {
    let s = 0, n = 0;
    for (let y = 0; y <= Math.min(r, h - 1); y++) { s += tmp[y * w + x]; n++; }
    for (let y = 0; y < h; y++) {
      out[y * w + x] = s / n;
      const add = y + r + 1, sub = y - r;
      if (add < h) { s += tmp[add * w + x]; n++; }
      if (sub >= 0) { s -= tmp[sub * w + x]; n--; }
    }
  }
  return out;
}

/** Rec.601 luma (0..1) of RGBA pixels. */
export function lumaOf(px: ArrayLike<number>, w: number, h: number): Float32Array {
  const l = new Float32Array(w * h);
  for (let i = 0, p = 0; i < w * h; i++, p += 4) l[i] = (0.299 * px[p] + 0.587 * px[p + 1] + 0.114 * px[p + 2]) / 255;
  return l;
}

/** Area-average downsample of a single-channel image. */
export function downsample(src: Float32Array, w: number, h: number, lw: number, lh: number): Float32Array {
  const out = new Float32Array(lw * lh);
  const fx = w / lw, fy = h / lh;
  for (let y = 0; y < lh; y++) {
    const y0 = Math.floor(y * fy), y1 = Math.max(y0 + 1, Math.floor((y + 1) * fy));
    for (let x = 0; x < lw; x++) {
      const x0 = Math.floor(x * fx), x1 = Math.max(x0 + 1, Math.floor((x + 1) * fx));
      let s = 0;
      for (let yy = y0; yy < y1; yy++) for (let xx = x0; xx < x1; xx++) s += src[yy * w + xx];
      out[y * lw + x] = s / ((y1 - y0) * (x1 - x0));
    }
  }
  return out;
}

function bilinear(src: Float32Array, lw: number, lh: number, u: number, v: number) {
  const x = Math.max(0, Math.min(lw - 1, u * lw - 0.5)), y = Math.max(0, Math.min(lh - 1, v * lh - 0.5));
  const x0 = Math.floor(x), y0 = Math.floor(y), x1 = Math.min(lw - 1, x0 + 1), y1 = Math.min(lh - 1, y0 + 1);
  const fx = x - x0, fy = y - y0;
  const a = src[y0 * lw + x0] * (1 - fx) + src[y0 * lw + x1] * fx;
  const b = src[y1 * lw + x0] * (1 - fx) + src[y1 * lw + x1] * fx;
  return a * (1 - fy) + b * fy;
}

/**
 * Fast guided filter: coefficients at the mask resolution, applied with the
 * full-resolution luma. Returns alpha 0..1 at w×h.
 */
export function guidedUpsample(
  mask: Float32Array, lw: number, lh: number, luma: Float32Array, w: number, h: number,
  r = Math.max(2, Math.round(lw / 120)), eps = GF_EPS,
  /** segmentation masks get the alpha ramp; a real matte (soft hair) keeps its values */
  ramp = true,
): Float32Array {
  const I = downsample(luma, w, h, lw, lh);
  const n = lw * lh;
  const Ip = new Float32Array(n), II = new Float32Array(n);
  for (let i = 0; i < n; i++) { Ip[i] = I[i] * mask[i]; II[i] = I[i] * I[i]; }
  const mI = boxMean(I, lw, lh, r), mP = boxMean(mask, lw, lh, r), mIp = boxMean(Ip, lw, lh, r), mII = boxMean(II, lw, lh, r);
  const A = new Float32Array(n), B = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const v = mII[i] - mI[i] * mI[i];
    A[i] = (mIp[i] - mI[i] * mP[i]) / (v + eps);
    B[i] = mP[i] - A[i] * mI[i];
  }
  const mA = boxMean(A, lw, lh, r), mB = boxMean(B, lw, lh, r);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const v = (y + 0.5) / h;
    for (let x = 0; x < w; x++) {
      const u = (x + 0.5) / w, i = y * w + x;
      const q = bilinear(mA, lw, lh, u, v) * luma[i] + bilinear(mB, lw, lh, u, v);
      out[i] = ramp ? alphaRamp(q) : Math.max(0, Math.min(1, q));
    }
  }
  return out;
}

const smoothstep = (e0: number, e1: number, x: number) => { const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

/**
 * Push-pull foreground estimate + edge decontamination, in place on RGBA
 * pixels. `alpha` (0..1 per pixel) is also written into the alpha channel.
 */
export function decontaminate(px: Uint8ClampedArray, alpha: Float32Array, w: number, h: number) {
  // level 0: premultiplied-by-weight colour of the interior
  type Lvl = { w: number; h: number; c: Float32Array };
  const c0 = new Float32Array(w * h * 4);
  for (let i = 0, p = 0; i < w * h; i++, p += 4) {
    const wt = smoothstep(INTERIOR - 0.07, INTERIOR, alpha[i]);
    c0[p] = px[p] * wt; c0[p + 1] = px[p + 1] * wt; c0[p + 2] = px[p + 2] * wt; c0[p + 3] = wt;
  }
  const levels: Lvl[] = [{ w, h, c: c0 }];
  // push: 2×2 sums down to a few pixels
  while (levels[levels.length - 1].w > 2 || levels[levels.length - 1].h > 2) {
    const f = levels[levels.length - 1];
    const lw = Math.max(1, Math.ceil(f.w / 2)), lh = Math.max(1, Math.ceil(f.h / 2));
    const c = new Float32Array(lw * lh * 4);
    for (let y = 0; y < f.h; y++) for (let x = 0; x < f.w; x++) {
      const s = (y * f.w + x) * 4, d = ((y >> 1) * lw + (x >> 1)) * 4;
      c[d] += f.c[s]; c[d + 1] += f.c[s + 1]; c[d + 2] += f.c[s + 2]; c[d + 3] += f.c[s + 3];
    }
    // normalise so the weight stays ≤ 1 (keeps colour ratios, makes "full" mean confident)
    for (let i = 0; i < lw * lh; i++) {
      const k = c[i * 4 + 3];
      if (k > 1) { c[i * 4] /= k; c[i * 4 + 1] /= k; c[i * 4 + 2] /= k; c[i * 4 + 3] = 1; }
    }
    levels.push({ w: lw, h: lh, c });
  }
  // pull: fill missing weight from the coarser level
  for (let l = levels.length - 2; l >= 0; l--) {
    const f = levels[l], g = levels[l + 1];
    for (let y = 0; y < f.h; y++) for (let x = 0; x < f.w; x++) {
      const s = (y * f.w + x) * 4, d = ((y >> 1) * g.w + (x >> 1)) * 4;
      const miss = 1 - f.c[s + 3];
      if (miss <= 0) continue;
      const gw = g.c[d + 3] || 1;
      f.c[s] += miss * g.c[d] / gw * Math.min(1, g.c[d + 3]);
      f.c[s + 1] += miss * g.c[d + 1] / gw * Math.min(1, g.c[d + 3]);
      f.c[s + 2] += miss * g.c[d + 2] / gw * Math.min(1, g.c[d + 3]);
      f.c[s + 3] += miss * Math.min(1, g.c[d + 3]);
    }
  }
  const c = levels[0].c;
  for (let i = 0, p = 0; i < w * h; i++, p += 4) {
    const a = alpha[i];
    const k = smoothstep(EDGE_LO, EDGE_HI, a);
    if (k < 1 && c[p + 3] > 1e-4) {
      const fw = c[p + 3];
      px[p] = c[p] / fw * (1 - k) + px[p] * k;
      px[p + 1] = c[p + 1] / fw * (1 - k) + px[p + 1] * k;
      px[p + 2] = c[p + 2] / fw * (1 - k) + px[p + 2] * k;
    }
    px[p + 3] = Math.round(a * 255);
  }
}
